<script setup lang="ts">
import { useWorkshops } from '~/composables/useWorkshops'
import SchoolPromoVideo from '~/components/school/SchoolPromoVideo.vue'

const { getWorkshops } = useWorkshops()
const route = useRoute()

const { data: workshops } = await useAsyncData('workshops', () => getWorkshops())

const paymentSuccess = computed(() => route.query.success === 'true')
const paymentError = computed(() => route.query.error === 'payment')

const { getAll: getAllVideos } = useSiteVideos()
const { data: siteVideos } = await useAsyncData('site-videos', () => getAllVideos())
const sectionVideoUrl = computed(() => siteVideos.value?.find(v => v.location === 'ateliers-section')?.videoUrl || '')

const { setPageSeo } = useSeo()
await setPageSeo({
    title: 'Ateliers d\'Été – Jeux Vidéo & 3D',
    description: 'Découvrez nos ateliers d\'été pour les 12 ans et plus : conception de jeux vidéo et modélisation 3D. Inscription en ligne.'
})
</script>

<template>
    <div class="bg-background text-foreground min-h-screen transition-colors duration-300">

        <UiPageHero
            pageKey="workshops"
            badge-text="Été 2026"
            title-prefix="ATELIERS"
            title-highlight="D'ÉTÉ"
            description="Plongez dans la création de jeux vidéo et la modélisation 3D lors d'ateliers intensifs encadrés par les équipes d'OXO Narbonne."
            :tags="['12 ANS ET +', 'DÉBUTANT', 'INTENSIF']"
            height="screen"
            align="left"
            :primary-button="{ text: 'VOIR LES ATELIERS', to: '#ateliers' }"
            :secondary-button="{ text: 'NOUS CONTACTER', to: '/contact' }"
        />

        <!-- Payment feedback -->
        <div v-if="paymentSuccess || paymentError" class="container mx-auto px-container-px mt-8">
            <div v-if="paymentSuccess" class="p-4 rounded-xl bg-green-500/10 border border-green-500/20 text-green-700 dark:text-green-400 flex items-center gap-3">
                <i class="fas fa-check-circle text-xl shrink-0" aria-hidden="true"></i>
                <span>Votre inscription a bien été confirmée ! Un email de confirmation vous a été envoyé.</span>
            </div>
            <div v-else class="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-700 dark:text-red-400 flex items-center gap-3">
                <i class="fas fa-exclamation-circle text-xl shrink-0" aria-hidden="true"></i>
                <span>Le paiement n'a pas abouti. Vous pouvez réessayer en cliquant sur "S'inscrire" ou reprendre votre inscription grâce au lien reçu par email.</span>
            </div>
        </div>

        <!-- Workshops list -->
        <section id="ateliers" class="py-24 bg-background">
            <div class="container mx-auto px-container-px">
                <UiSectionHeader
                    title-prefix="PROCHAINS"
                    title-highlight="ATELIERS"
                    subtitle="Choisissez l'atelier qui correspond à votre passion !"
                    align="center"
                />

                <div v-if="!workshops || workshops.length === 0" class="text-center text-foreground-dim py-16">
                    <i class="fas fa-calendar-xmark text-4xl mb-4 opacity-40" aria-hidden="true"></i>
                    <p>Aucun atelier disponible pour le moment. Revenez bientôt !</p>
                </div>

                <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    <WorkshopsWorkshopCard
                        v-for="workshop in workshops"
                        :key="workshop.id"
                        :workshop="workshop"
                    />
                </div>
            </div>
        </section>

        <!-- Au programme -->
        <section class="py-24 bg-background border-y border-oxo-border transition-colors duration-300">
            <div class="container mx-auto px-container-px">
                <UiSectionHeader
                    title-prefix="AU"
                    title-highlight="PROGRAMME"
                    align="center"
                />
                <div class="grid grid-cols-1 sm:grid-cols-3 gap-12 text-center">
                    <div>
                        <div class="w-16 h-16 rounded-full bg-oxo-red/10 text-oxo-red flex items-center justify-center mx-auto mb-6 text-2xl">
                            <i class="fas fa-gamepad" aria-hidden="true"></i>
                        </div>
                        <h3 class="font-display font-bold text-xl text-foreground mb-3 uppercase">Conception de Jeux</h3>
                        <p class="text-foreground-dim text-sm leading-relaxed">Game design et programmation Unity : de l'idée au jeu jouable.</p>
                    </div>
                    <div>
                        <div class="w-16 h-16 rounded-full bg-oxo-red/10 text-oxo-red flex items-center justify-center mx-auto mb-6 text-2xl">
                            <i class="fas fa-cube" aria-hidden="true"></i>
                        </div>
                        <h3 class="font-display font-bold text-xl text-foreground mb-3 uppercase">Modélisation 3D</h3>
                        <p class="text-foreground-dim text-sm leading-relaxed">Création de modèles 3D avec Blender, initiation aux textures et au rendu.</p>
                    </div>
                    <div>
                        <div class="w-16 h-16 rounded-full bg-oxo-red/10 text-oxo-red flex items-center justify-center mx-auto mb-6 text-2xl">
                            <i class="fas fa-users" aria-hidden="true"></i>
                        </div>
                        <h3 class="font-display font-bold text-xl text-foreground mb-3 uppercase">Encadrement Pro</h3>
                        <p class="text-foreground-dim text-sm leading-relaxed">Formateurs issus de l'industrie, petits groupes pour un suivi personnalisé.</p>
                    </div>
                </div>

                <!-- Voir les projets réalisés -->
                <div class="mt-12 text-center">
                    <a
                        href="https://oxo-ateliers.itch.io/"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="btn-secondary inline-flex items-center gap-2"
                    >
                        <i class="fab fa-itch-io" aria-hidden="true"></i>
                        Voir les jeux créés lors des éditions précédentes
                    </a>
                </div>
            </div>
        </section>

        <SchoolPromoVideo
            v-if="sectionVideoUrl"
            :video-url="sectionVideoUrl"
        />

        <AppCTA
            title="Une question"
            title-highlight="SUR LES ATELIERS&nbsp;?"
            subtitle="Nous sommes disponibles pour répondre à toutes vos questions."
            button-text="Nous contacter"
            button-link="/contact"
            info-text=""
        />

    </div>
</template>
